// Copyright ©️ 2026 eVolve MEP, LLC

import { type FocusEvent, type ReactNode, useCallback, useId } from 'react';

import { Box, Group, Input, UnstyledButton } from '@mantine/core';

import classes from 'components/Mantine/AngleInput.module.css';
import { isNotNil } from 'helpers/isNotNil';
import { useCommittedNumberInput } from 'hooks/useCommittedNumberInput';

export type AngleInputProps = {
  /** In degrees. */
  value?: number | null;
  onChange: (value: number | null) => void;
  /** Standard angles offered as quick-fill buttons under the field, in degrees. A tap applies the angle at once. */
  quickFillAngles: readonly number[];
  /** Smallest allowed angle, in degrees. Typed angles are clamped to it. */
  min: number;
  /** Largest allowed angle, in degrees. Typed angles are clamped to it. */
  max: number;
  /**
   * Number of decimal places a typed angle is rounded to.
   * @defaultValue 2
   */
  decimalPlaces?: number;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /** Called after Enter commits a typed angle. */
  onSubmit?: () => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

/**
 * A touch-sized angle field with a row of quick-fill buttons for standard angles.
 *
 * The field shows the angle with its unit (`37.5°`). Typing accepts a number with or without `°`; Enter or leaving
 * the field commits it, rounded and clamped to `min`–`max`, and Escape reverts it. The quick-fill button matching the
 * current angle is tinted.
 */
export const AngleInput = ({
  value,
  onChange,
  quickFillAngles,
  min,
  max,
  decimalPlaces = 2,
  label,
  description,
  error,
  required,
  disabled,
  readOnly,
  placeholder,
  autoFocus,
  onSubmit,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
}: AngleInputProps) => {
  const parse = useCallback(
    (text: string) => {
      const degrees = parseFloat(text.replaceAll(/[^\d.-]/g, ''));
      if (!Number.isFinite(degrees)) return null;
      const scale = 10 ** decimalPlaces;
      return Math.min(max, Math.max(min, Math.round(degrees * scale) / scale));
    },
    [min, max, decimalPlaces],
  );

  const { inputProps } = useCommittedNumberInput({
    value,
    // Rounded for display, since a value set from outside (e.g. a server calculation) can carry float noise.
    format: (degrees) => `${Number(degrees.toFixed(decimalPlaces))}°`,
    parse,
    onCommit: onChange,
    onFocus,
    onBlur,
    onSubmit,
  });

  const id = useId();
  const locked = disabled || readOnly;
  const hasError = isNotNil(error) && error !== false;

  return (
    <Input.Wrapper
      id={id}
      label={label}
      description={description}
      error={error}
      required={required}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Box className={classes.fieldFrame} data-error={hasError || undefined} data-disabled={locked || undefined}>
        <Box
          component="input"
          id={id}
          className={classes.field}
          {...inputProps}
          inputMode="decimal"
          enterKeyHint="done"
          autoComplete="off"
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          readOnly={readOnly}
        />
      </Box>
      <Group className={classes.quickFill} gap={8} wrap="nowrap">
        {quickFillAngles.map((angle) => (
          <UnstyledButton
            key={angle}
            className={classes.quickFillButton}
            data-active={value === angle || undefined}
            disabled={locked}
            onClick={() => {
              if (angle !== value) onChange(angle);
            }}
          >
            {angle}°
          </UnstyledButton>
        ))}
      </Group>
    </Input.Wrapper>
  );
};
